<template>
  <button
    :class="['bc-button-container relative duration-300 ease-in-out', border, cursorType, btnPadding, eventClass]"
    @click.capture="handleClick"
    :type="submit ? 'submit' : 'button'"
    :disabled="disabled"
  >
    <div
      v-if="loading"
      class="absolute"
    >
      <BcLottie
        :path="loadingLottiePath"
      />
    </div>

    <div :class="['button-wrapper flex items-center gap-2e', loading ? 'opacity-0' : 'opacity-100']">
      <div
        v-if="isIcon"
        class="left-wrapper"
      >
        <BcIcon
          v-if="leftIcon"
          :icon="leftIcon"
          size="1.5em"
          :color="color"
          :svg="svg"
        />
      </div>
      <div class="content-wrapper">
        <BcIcon
          v-if="icon"
          :icon="icon"
          size="1.5em"
          :color="color"
          :svg="svg"
        />
        <div
          v-if="$slots.default || upText || downText"
          :class="['text-wrapper']"
        >
          <span class="sub-text">{{ upText }}</span>
          <div
            v-if="$slots.default"
            class="text"
          >
            <slot />
          </div>
          <span class="sub-text">
            {{ downText }}
          </span>
        </div>
      </div>
      <div
        v-if="isIcon"
        class="right-wrapper"
      >
        <BcIcon
          v-if="rightIcon"
          :icon="rightIcon"
          size="1.5em"
          :color="color"
          :svg="svg"
        />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">

import { computed } from 'vue'

import { getGAEventName } from '../../utils/ga.util'
import BcIcon from '../icon/BcIcon.vue'
import BcLottie from '../lottie/BcLottie.vue'
import { BcButtonProps } from './button.types'


const props = withDefaults(defineProps<BcButtonProps>(), {
  icon: undefined,
  type: 'solid',
  leftIcon: undefined,
  rightIcon: undefined,
  upText: undefined,
  downText: undefined,
  color: 'absoluteWhite',
  backgroundColor: 'theme',
  disabled: false,
  submit: false,
  loading: false,
  dropdownBtn: false,
  svg: true,
})

const emit = defineEmits([ 'click' ])

const bgColor = computed(()=>{
  if (props.type === 'solid')
    return `rgb(var(--bc-${props.backgroundColor}))`

  return  'transparent' 
})

const textColor = computed(()=>{
  if (props.type === 'line' || props.type === 'glass'){
    if (props.color === 'absoluteWhite')
      return 'rgb(var(--bc-dark))'
  }
  return `rgb(var(--bc-${props.color}))`
})

const cursorType = computed(()=>{
  if (props.disabled)
    return 'cursor-not-allowed select-none opacity-20'

  return 'cursor-pointer'
})

const border = computed(()=>{
  if (props.type === 'line')
    return 'border border-dark/20'

  return ''
})

const hoverColor = computed(()=>{
  if (props.type === 'solid')
    return `rgba(var(--bc-${props.backgroundColor}), 0.8)`

  if (props.type === 'glass')
    if (props.color === 'absoluteWhite') return 'rgba(var(--bc-dark), 0.1)'
    else return `rgba(var(--bc-${props.color}), 0.1)`
  return  'rgba(var(--bc-dark), 0.1)'
})

const activeColor = computed(()=>{
  if (props.type === 'solid')
    return `rgba(var(--bc-${props.backgroundColor}), 0.4)`

  if (props.type === 'glass')
    return `rgba(var(--bc-${props.color}), 0.2)`
  return  'rgba(var(--bc-dark), 0.2)'
})

const isIcon = computed(()=>{
  return  props.leftIcon || props.rightIcon
})

const btnJustify = computed(()=>{
  if (isIcon.value)
    return 'space-between'

  return 'center'
})

const btnPadding = computed(()=>{
  if (props.dropdownBtn){
    return 'py-1e px-2e'
  }
  return 'p-2e'
})

const eventClass = computed(()=>{
  return getGAEventName(props.gaEventName)
})

const loadingLottiePath = computed(()=>{
  const path = 'https://globalstatic.xangle.io/lottie'
  if (props.backgroundColor === 'white' || props.backgroundColor === 'absoluteWhite' || bgColor.value === 'transparent')
    return `${path}/processing_thema.json`
  return `${path}/processing_white.json`
})



const handleClick = () =>{
  if (props.loading) return
  if (!props.disabled)
    emit('click')
}


</script>

<style scoped lang="scss">
.bc-button-container{
  @apply flex gap-2e rounded-2e items-center;
  color: v-bind(textColor);
  background-color: v-bind(bgColor);
  justify-content: v-bind(btnJustify);
  height: fit-content;

  
  &:hover{
    background-color: v-bind(hoverColor);
  }
  &:active{
    background-color: v-bind(activeColor);
  }
  .button-wrapper{
    //transition: opacity 0.2s ease-in-out;
    .left-wrapper{
      @apply flex items-center;
    }
    .content-wrapper{
      @apply flex gap-2e items-center;

      .text-wrapper{
        @apply flex flex-col font-bold;

        .sub-text{
          @apply text-3e;
        }
        .text{
          @apply flex text-4e;
        }
      }
    }
    .right-wrapper{
      @apply flex items-center;
    }
  }

}
</style>
